import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getAlcomScoreByServiceIds as onGetAlcomScoreByServiceIds,
  getServices as onGetServices,
  getAlertLogs,
  getEndpointByServiceId as onGetEndpointByServiceId,
  getTimestamps as onGetTimestamps,
  getTimestampsByServiceId as onGetTimestampsByServiceId
} from "../../store/actions";
import Card from "components/PurpleTheme/Card";
import CardItem from "components/PurpleTheme/CardItem";
import ReactApexChart from "react-apexcharts";
import { Row, Col, UncontrolledTooltip } from "reactstrap";
import { Link } from "react-router-dom";
import LineChart from "./LineChart";
import "./styles.scss"

const SloDashboard = () => {
  const dispatch = useDispatch();
  const [serviceIds, setServiceIds] = useState([]);
  const [itemHover, setItemHover] = useState(false);
  const [currentHoverItem, setCurrentHoverItem] = useState(null);
  const [details, showDetails] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [allData, setAllData] = useState({});
  const [sortedData, setSortedData] = useState({});
  const [currentMS, setCurrentMS] = useState(null);
  const [alertsOpen, setAlertsOpen] = useState({});
  const [currentEndpoint, setCurrentEndpoint] = useState(null);
  const [timestampsForEndpoints, setTimestampsForEndpoint] = useState([]);


  const { alcomScoresByServiceIds } = useSelector(state => ({
    alcomScoresByServiceIds: state.Dashboard.AlcomScores || {}
  }));

  const { alerts } = useSelector(state => ({ alerts: state.AlertLog.alerts }));

  const { services } = useSelector(state => ({
    services: state.Service.services
  }));

  const { endpointDetails } = useSelector(state => ({ endpointDetails: state.PingMonitor.endpointDetails}));

  const { timestamps } = useSelector(state => ({ timestamps: state.PingMonitor.timestamps}));

  const { timestamps_by_service } = useSelector(state => ({ timestamps_by_service: state.PingMonitor.timestamps_by_service}));

  const headers = ['Service name', 'ALCOM Score', 'ALCOM Status', 'Uptime Status', 'Alerts Open'];
  const MS = {
    'Amazon Web Services' : 'AWS',
    'Google Cloud Platform' : 'GCP',
    'New Relic' : 'New Relic',
    'Microsoft Azure' : 'Azure',
    'Datadog': 'Datadog',
    'Splunk' : 'Splunk',
    'App Dynamics' : 'AppDynamics',
    'Dynatrace' : 'Dynatrace'
  }

  useEffect(() => {
    dispatch(onGetServices());
    dispatch(getAlertLogs({'page': 1}));
  }, [dispatch, getAlertLogs, onGetServices]);

  useEffect(() => {
    if (services && services.length > 0) {
      const ids = services.map(service => service.id);
      setServiceIds(ids);
    }
  }, [services]);

  useEffect(() => {
    if (serviceIds.length > 0) {
      dispatch(onGetEndpointByServiceId({ 'application_service_ids': serviceIds }));
    }
  }, [serviceIds, onGetEndpointByServiceId]);

  useEffect(() => {
    const serviceCounts = {};
    
    alerts.forEach((alert) => {
      const serviceId = alert.application_service.id;
      if(alert.status !== 'Ok') {
        if (serviceCounts[serviceId]) {
          serviceCounts[serviceId]++;
        } else {
          serviceCounts[serviceId] = 1;
        }
      }
      
    });
    setAlertsOpen(serviceCounts)
  }, [alerts, services]);

  useEffect(() => {
    if (endpointDetails[currentHoverItem] && Array.isArray(endpointDetails[currentHoverItem])) {
      setCurrentEndpoint(endpointDetails[currentHoverItem][0]?.id || null);
    } else {
      setCurrentEndpoint(null);
    }
  }, [currentHoverItem, endpointDetails]);

  useEffect(() => {
    if (serviceIds.length > 0) {
      dispatch(onGetAlcomScoreByServiceIds({service_ids: serviceIds}));
      dispatch(onGetTimestampsByServiceId({application_service_ids: serviceIds}));
    }
  }, [dispatch, serviceIds]);

  useEffect(() => {
    const structuredData = {};
  
    timestamps_by_service.forEach((timestamp) => {
      const { application_service_id, application_id, ...rest } = timestamp;
  
      if (!structuredData[application_service_id]) {
        structuredData[application_service_id] = {};
      }
  
      if (!structuredData[application_service_id][application_id]) {
        structuredData[application_service_id][application_id] = [];
      }
  
      structuredData[application_service_id][application_id].push(rest);
    });
  
    setTimestampsForEndpoint(structuredData);

  }, [timestamps_by_service]);

  useEffect(() => {
    const data = {};
  
    Object.entries(alcomScoresByServiceIds).forEach(([serviceId, serviceData]) => {
      const actual = [];
      const expected = [];
      const monitoring_systems = [];
      const labels = [];
      const alcom = {}
      Object.entries(serviceData.monitoring_systems).forEach(([systemName, systemData]) => {
        actual.push(systemData.actual_alerts);
        expected.push(systemData.expected_alerts);
        labels.push(systemName);
        monitoring_systems.push(systemName);
        alcom[systemName] = systemData.alcom_score;
      });
      setCurrentMS[monitoring_systems[0]];
      data[serviceId] = { actualAlerts: actual, expectedAlerts: expected, monitoring_systems: monitoring_systems, labels: labels, alcom: alcom};
      
    });
    setAllData(data);
  }, [alcomScoresByServiceIds]);

  useEffect(() => {
    const dataSorted = {};
    const alcom_scores = Object.entries(alcomScoresByServiceIds).map(([serviceId, serviceData]) => ({
      serviceId,
      ...serviceData
    }));

    // Sort alcom_scores based on the given logic
    const sortedAlcomScores = alcom_scores.sort((a, b) => {
      const aCondition = a.alcom_score === 0 && a.expected_alerts === 0;
      const bCondition = b.alcom_score === 0 && b.expected_alerts === 0;
      if (aCondition && !bCondition) return 1;
      if (!aCondition && bCondition) return -1;
      return a.alcom_score - b.alcom_score;
    });

    // Store the sorted data in dataSorted
    sortedAlcomScores.forEach((item) => {
      dataSorted[item.serviceId] = item;
    });

    setSortedData(dataSorted);
  }, [alcomScoresByServiceIds]);
  

  const handleViewMoreClick = (serviceId) => {
    if(endpointDetails[serviceId]) {
      setCurrentEndpoint(endpointDetails[serviceId][0].id)
    }

    if (selectedItem !== serviceId) {
      setSelectedItem(serviceId);
      showDetails(true);
      setCurrentMS(allData[serviceId].monitoring_systems[0]);
      if (endpointDetails[serviceId]){
        dispatch(onGetTimestamps({
          'endpoint_id': currentEndpoint,
          'duration_in_minutes': 60
        }));
      }
    } else {
      showDetails(!details);
    }
  };

  const handleEndpointChangeClick = (endpoint_id) => {
    setCurrentEndpoint(endpoint_id)
    dispatch(onGetTimestamps({
      'endpoint_id': endpoint_id,
      'duration_in_minutes': 60
    }));
  }

  function getStatus (actualAlerts, expectedAlerts) {
    if(actualAlerts === expectedAlerts){
      return 'bg-success'
    } else if(actualAlerts > 0) {
      return 'bg-warning'
    } else {
      return 'bg-danger'
    }
  }

  const determineStatus = (endpoints) => {
    if (!Array.isArray(endpoints)) {
      return 'unknown';
    }
    const hasFalse = endpoints.some(ep => !ep.last_ping_successful);
    const hasTrue = endpoints.some(ep => ep.last_ping_successful);
  
    if (hasFalse && hasTrue) return 'bg-warning';
    if (hasTrue) return 'bg-success';
    return 'bg-danger';
  };
  
  const setTimeframe = (hours) => {
    const timeframeInMinutes = hours * 60;
    dispatch(onGetTimestamps({
      'endpoint_id': currentEndpoint,
      'duration_in_minutes': timeframeInMinutes
    }))
  }

  const transformedData = Object.keys(endpointDetails).reduce((acc, serviceId) => {
    const endpoints = endpointDetails[serviceId];
    acc[serviceId] = endpoints.map(endpoint => ({
      last_ping_successful: endpoint.last_ping_successful
    }));
    
    return acc;
  }, {});

  const colorList = [
    '#F76E65',  
    '#BB1A54',  
    '#4911C0', 
    '#148cef',
    '#502B80',
    '#2A1FA4',
    '#E6D1D1' 
  ];

  function pieOptions(labelList) {
    const options = {
      chart: {
        width: 20,
        type: 'donut',
      },
      labels: labelList || [],
      colors: colorList,
      stroke: {
        width: 0 
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '10px',
          fontFamily: 'Helvetica, Arial, sans-serif',
          fontWeight: 'small',
          colors: ['#000'],
        },
        dropShadow: {
          enabled: false,
          top: 1,
          left: 1,
          blur: 1,
          opacity: 0.45
        },
        formatter: function (val) {
          return val.toFixed(1) + "%";
        },
        offsetX: 10,
        offsetY: 10, 
      },
      fill: {
        opacity: 0.94 
    },
      plotOptions: {
        pie: {
          donut: {
            size: '80%'
          }
        }
      },
      legend: {
        position: 'right',
        fontSize: '12px',
        labels: {
          colors: ['#333'],
          useSeriesColors: false
        },
      },
      tooltip: {
        enabled: true,
        style: {
          fontSize: '14px',
          fontFamily: 'Helvetica, Arial, sans-serif',
        },
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: {
            width: 200
          },
          legend: {
            position: 'bottom'
          }
        }
      }]
    };
    return options;
  }
  
  const options = {
    chart: {
      height: 280,
      type: "radialBar",
      toolbar: {
        show: true
      }
    },
    plotOptions: {
      radialBar: {
        startAngle: -135,
        endAngle: 135,
        hollow: {
          margin: 0,
          size: "80%",
          background: "none",
          dropShadow: {
            enabled: true,
            top: 3,
            left: 0,
            blur: 4,
            opacity: 0.24
          }
        },
        track: {
          background: '#3e2e4e',
          strokeWidth: '70%',
          opacity: 0.3,
          margin: 0,
          dropShadow: {
            enabled: false,
            top: -3,
            left: 0,
            blur: 4,
            opacity: 0.35
          }
        },
        dataLabels: {
          name: {
            offsetY: 45,
            color: "#fff",
            fontSize: "12px"
          },
          value: {
            offsetY: 65,
            color: "#fff",
            fontSize: "28px",
            show: true,
            formatter: function(val) {
              return parseInt(val);
            }
          }
        }
      }
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'dark',
        type: 'horizontal',
        shadeIntensity: 0.5,
        colors:['#FD5392'],
        gradientToColors: ['#F76E65'],
        inverseColors: true,
        opacityFrom: 1,
        opacityTo: 1,
        stops: [0, 100]
      }
    },
    stroke: {
      lineCap: 'round',
      strokeWidth: "50%",
    },
    labels: ["ALCOM score"]
  };
  
  function optionsBar(monitoring_systems) {
    const options = {
        xaxis: {
            categories: monitoring_systems,
        },
        yaxis: {
            title: {
                text: 'Alerts'
            }
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '35%',
                borderRadius: 5,
            },
        },
        dataLabels: {
            enabled: false
        },
        colors: ['#F76E65', '#4911C0'],
        stroke: {
            show: true,
            width: 2,
            colors: ['transparent']
        },
        fill: {
          type: 'gradient',
          gradient: {
              shade: 'dark',
              type: 'vertical',
              shadeIntensity: 0.5, 
              gradientToColors: ['#F76E65', '#4911C0', '#FD5392', '#008efa'],
              inverseColors: false,
              opacityFrom: 0.85,
              opacityTo: 0.85,
              stops: [0, 100],
          }
      },
        tooltip: {
            y: {
                formatter: function (val) {
                    return val + " alerts";
                }
            }
        }
    };
    return options;
  }

  return (
    <React.Fragment>
      <div className="w-100">
        <h5 className="mb-2">SLO Dashboard</h5>
        <div className="header d-flex justify-content-evenly">
          {headers.map((item, index) => (
            <Col lg={item === 'Service name' ? 3 : 2} key={index}>
              <div className="text-center p-2 py-3">
                {item}
              </div>
            </Col>
          ))}
        </div>

        <div className="card-container">
          {Object.entries(sortedData).map(([serviceId, value]) => (
            <>
              {(!details || selectedItem === serviceId) && (
                <CardItem
                  padding={`${endpointDetails[serviceId] ? '0rem' : '0.5rem'}`}
                  cursor="pointer"
                  bgColor={itemHover && (serviceId === currentHoverItem) && !details ? '#382d45' : ''}
                  key={serviceId}
                  border={itemHover && (serviceId === currentHoverItem) && !details ? '' : ''}
                  className={`card-item ${selectedItem === serviceId ? 'expanded' : ''}`}
                >
                  <div
                    onClick={() => {handleViewMoreClick(serviceId); }}
                    onMouseEnter={() => { setItemHover(true); setCurrentHoverItem(serviceId); }}
                    onMouseLeave={() => { if (!details) { setItemHover(false); setCurrentHoverItem(null); } }}
                    className={`d-flex justify-content-evenly text-center py-3 w-100 px-0`}
                  >
                    <Col lg={3}>
                    <div className="w-100 text-center d-flex align-items-center justify-content-between gap-2 mx-4 px-5">
                      <span className="w-75 text-start resource-name-truncate">
                        {value.service.name}
                      </span>
                      <Link
                        className="font-weight-bold font-size-14 w-25"
                        to={`/temperstack-notifications/${value.service.id}`}
                      >
                        <span id={`service-${serviceId}`}>
                          <i className="bx bx-link font-size-15" />
                        </span>
                        <UncontrolledTooltip className="tooltip-component-slo fix-flicker"
                                             placement="top"
                                             target={`service-${serviceId}`}>View Service details</UncontrolledTooltip>
                      </Link>
                      </div>
                    </Col>
                    <Col lg={2}>{value.alcom_score}%</Col>
                    <Col lg={2}>
                      <div className="d-flex justify-content-center align-items-center">
                        <div
                          id={`status-${serviceId}`}
                          className={`text-center h-4 w-4 rounded-circle bg-primary ${getStatus(value.actual_alerts, value.expected_alerts)}`}
                          style={{ height: "18px", width: "18px" }}
                        ></div>
                        <UncontrolledTooltip placement="top" target={`status-${serviceId}`}>{value.expected_alerts - value.actual_alerts} missing alert{value.expected_alerts - value.actual_alerts !== 1 && 's'}</UncontrolledTooltip>
                      </div>
                    </Col>
                    <Col className="p-0 m-0" lg={2}>
                      {endpointDetails[serviceId] ? 
                        <div
                          className={`text-center h-4 w-4 rounded-circle bg-primary ${determineStatus(transformedData[serviceId])}`}
                          style={{ height: "18px", width: "18px" }}>
                        </div>
                        :
                        <div>
                          -
                        </div>
                      }
                      
                    </Col>
                    <Col lg={2}>
                      <span>{alertsOpen[serviceId] || '0'}</span>
                      {itemHover && (serviceId === currentHoverItem) && (
                        <button
                          className="ms-2 btn btn-primary py-1 rounded-pill"
                        >
                          {details ? (
                            <div className="d-flex gap-1 align-items-center px-1">
                              <span>View less</span>
                              <i className="bx bx-chevron-up" />
                            </div>
                          ) : (
                            <div className="d-flex gap-1 align-items-center px-1">
                              <span>View details</span>
                              <i className="bx bx-chevron-down" />
                            </div>
                          )}
                        </button>
                      )}
                    </Col>
                  </div>
                </CardItem>
              )}

              {details && (selectedItem === serviceId) && (
                <div className="details d-flex flex-column">
                  <div className="d-flex flex-row gap-3">
                    <div className="d-flex flex-column gap-3" style={{ height: "260px" }}>
                      <div className="d-flex gap-3 h-50">
                        <div
                          className="custom-color-card"
                          style={{ background: "linear-gradient(90deg, rgba(247,110,101,1) 0%, rgba(249,99,119,1) 73%, rgba(253,83,146,1) 100%)" }}
                        >
                          <h1>{alertsOpen[serviceId] || 0}</h1>
                          <span>{alertsOpen[serviceId] !== 1 ? 'Alerts open' : 'Alert open'}</span>
                        </div>
                        <div className="custom-color-card">
                          <h1>{value.expected_alerts - value.actual_alerts}</h1>
                          <span>Missing Alerts</span>
                          <div
                            className="d-flex rounded-circle justify-content-center align-items-center"
                            style={{ height: "28px", width: "28px", background: "#f66b69" }}
                          >
                            <i className="dripicons-warning m-0 m-auto mt-1 font-size-14" />
                          </div>
                        </div>
                      </div>
                      <div className="w-100 d-flex gap-3 h-50">
                        <div className="custom-color-card">
                          <h1>{value.actual_alerts}</h1>
                          <span>Alerts deployed</span>
                        </div>
                        <div className="custom-color-card">
                          <h1>{value.expected_alerts}</h1>
                          <span>Expected alerts</span>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex flex-column w-100 gap-2">
                      <Card height="260px">
                        <div className="w-100 h-100 justify-content-center">
                          <div className="d-flex align-items-center h-full mb-3">
                            <i className="icon-box bx bx-bell" />
                            <h6 className="mb-0 font-size-14">Alerts deployed by monitoring system</h6>
                          </div>
                            {value.actual_alerts != 0 ? 
                            <>
                            {allData && allData[serviceId] && (
                                <ReactApexChart
                                  options={pieOptions(allData[serviceId].labels)}
                                  series={allData[serviceId].actualAlerts}
                                  type="donut"
                                  height="180"
                                  className="apex-charts m-auto"
                                />
                              )}
                            </>   : 
                            <div className="d-flex h-full justify-content-center align-items-center">
                              <h5 className="mt-5">No Alerts deployed</h5>
                            </div>
                        }
                          
                        </div>
                      </Card>
                    </div>
                  </div>
                  <div className="d-flex justify-content-center gap-3">
                    <div style={{ width: "36%" }}>
                      <Card minHeight="360px">
                        {allData[serviceId] && (
                          <>
                            <div className="d-flex flex-wrap">
                              {allData[serviceId].monitoring_systems.map((item, index) => (
                                <button
                                  className="btn btn-slo"
                                  style={{ backgroundColor: `${currentMS === item ? '#3e2e4e' : ''}` }}
                                  key={index}
                                  onClick={() => setCurrentMS(item)}
                                >
                                  {MS[item]}
                                </button>
                              ))}
                            </div>

                            <ReactApexChart
                              options={options}
                              series={[allData[serviceId].alcom[currentMS]]}
                              type="radialBar"
                              height="270"
                              className="apex-charts mb-3"
                            />
                            <div className="w-full d-flex align-items-center justify-content-center h-full mt-4">
                              <i className="icon-box mdi mdi-gauge" style={{ padding: "1px 5px 1px" }} />
                              <h6 className="mb-0 font-size-14">Alcom Score by Monitoring System</h6>
                            </div>
                          </>
                        )}
                      </Card>
                    </div>
                    <div style={{ width: "64%" }}>
                      <Card height="360px">
                        <ReactApexChart
                          options={optionsBar(allData[serviceId].monitoring_systems)}
                          series={[
                            {
                              name: "Actual",
                              data: allData[serviceId].actualAlerts,
                            },
                            {
                              name: "Expected",
                              data: allData[serviceId].expectedAlerts,
                            },
                          ]}
                          type="bar"
                          height="270"
                          className="apex-charts"
                        />
                        <div className="w-full d-flex align-items-center justify-content-center h-full my-3">
                          <i className="icon-box dripicons-graph-bar" style={{ padding: "4px 6px 0px" }} />
                          <h6 className="mb-0 font-size-14">Actual Alerts vs Expected Alerts</h6>
                        </div>
                      </Card>
                    </div> 
                  </div>
                  {endpointDetails[currentHoverItem] ? 
                    <Card>
                      <div>
                        {endpointDetails[currentHoverItem].map((item, index) => (
                          <div 
                            className="btn btn-slo"
                            style={{ backgroundColor: `${currentEndpoint === item.id ? '#3e2e4e' : ''}` }} 
                            onClick={() => handleEndpointChangeClick(item.id)}
                            key={index}>
                              {item.name}
                          </div>
                        ))}
                      </div>
                    <LineChart height={'280'} chartType={'line'} disableFilters={false} setTimeframe={setTimeframe} timeFrame={1} data={timestamps} />
                  </Card> : null  
                    }
                  
                </div>
              )}
            </>
          ))}
        </div>
      </div>
    </React.Fragment>

  );
};

export default SloDashboard;
